import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { setActiveCompanyIdGetter } from "@/lib/api";
import { useCompanies } from "@/lib/api/companies.queries";
import type { CompanySummary } from "@/lib/api/companies";
import { useActiveTeam } from "@/lib/team-context";

const ACTIVE_COMPANY_KEY = "pahfacturar.app.activeCompanyId";

type CompanyContextValue = {
  /** Currently selected company id, or null if none picked / list empty. */
  activeCompanyId: string | null;
  /** Currently selected company summary, or null if list has not loaded. */
  activeCompany: CompanySummary | null;
  /** Pick a company. Persisted to localStorage and propagated to lib/api. */
  setActiveCompanyId: (companyId: string | null) => void;
  /** Has the company list initial fetch completed yet? */
  ready: boolean;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

function readPersisted(): string | null {
  try {
    return localStorage.getItem(ACTIVE_COMPANY_KEY);
  } catch {
    return null;
  }
}

function persist(value: string | null) {
  try {
    if (value) localStorage.setItem(ACTIVE_COMPANY_KEY, value);
    else localStorage.removeItem(ACTIVE_COMPANY_KEY);
  } catch {
    // Storage might be disabled (private mode); silently ignore.
  }
}

/**
 * CompanyProvider — single source of truth for the "currently active
 * company" within the active team. Mounts INSIDE TeamProvider in __root.tsx.
 *
 * Hydration order:
 *   1. read localStorage on mount (instant — survives reloads)
 *   2. once `useCompanies()` resolves under the active team, sanity-check
 *      the persisted id is still in the team's company list; if not, fall
 *      back to the first company in the list, else null.
 *
 * Reset rule: when the active team changes, the persisted active company
 * is cleared so the user does not accidentally operate on a company under
 * the wrong team after switching.
 *
 * Side effect: registers a getter with lib/api so authenticated fetches on
 * /api/me/dian/* paths automatically attach `X-Company-Id`. Routes under
 * /api/me/companies/:companyId/* set the header explicitly via the typed
 * wrappers (lib/api/companies.ts) — they don't depend on this getter.
 */
export function CompanyProvider({ children }: { children: ReactNode }) {
  const { activeTeamId, ready: teamReady } = useActiveTeam();
  const companiesQuery = useCompanies();
  // Same hydration policy as TeamProvider: never seed from localStorage on
  // the first render. A stale companyId would leak into the X-Company-Id
  // header before the hydrate effect can validate it against the team's
  // actual list.
  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(null);
  const [hydratedForTeam, setHydratedForTeam] = useState<string | null>(null);

  // Wire lib/api to read the active company id on every request. Same timing
  // contract as TeamProvider: register the getter ONCE and read a ref kept in
  // sync DURING RENDER, not via a `[activeCompanyId]` effect. A descendant
  // query that gates on the active company fires its fetch in a passive effect
  // that runs child-before-parent — an effect-registered getter would lag one
  // commit and ship the auto-injected X-Company-Id as stale/null, yielding a
  // 400 NO_COMPANY_CONTEXT that retry:false never recovers from. The render
  // write is current before any effect runs.
  const activeCompanyIdRef = useRef<string | null>(activeCompanyId);
  activeCompanyIdRef.current = activeCompanyId;
  useEffect(() => {
    setActiveCompanyIdGetter(() => activeCompanyIdRef.current);
    return () => {
      setActiveCompanyIdGetter(() => null);
    };
  }, []);

  // Hydrate the active company once the company list comes in for the
  // current team. Re-hydrates when the user switches teams (so we don't
  // leak a company from team A into team B). Until hydration completes,
  // `activeCompanyId` stays null and the X-Company-Id header is never
  // sent — downstream queries that gate on `ready` see no false positives.
  useEffect(() => {
    if (!teamReady) return;
    if (!activeTeamId) {
      // No active team → no active company. Mark hydrated so consumers can
      // render the "pick a team first" empty state.
      if (activeCompanyId !== null) {
        setActiveCompanyIdState(null);
        persist(null);
      }
      setHydratedForTeam(null);
      return;
    }
    if (hydratedForTeam === activeTeamId) return;
    if (companiesQuery.status !== "success") return;
    const companies = companiesQuery.data?.companies ?? [];
    const knownIds = new Set(companies.map((c) => c.id));
    const persisted = readPersisted();
    let next: string | null;
    if (persisted && knownIds.has(persisted)) {
      next = persisted;
    } else {
      next = companies[0]?.id ?? null;
    }
    if (next !== persisted) persist(next);
    setActiveCompanyIdState(next);
    setHydratedForTeam(activeTeamId);
  }, [
    teamReady,
    activeTeamId,
    hydratedForTeam,
    companiesQuery.status,
    companiesQuery.data,
    activeCompanyId,
  ]);

  const setActiveCompanyId = useCallback((companyId: string | null) => {
    setActiveCompanyIdState(companyId);
    persist(companyId);
  }, []);

  const activeCompany = useMemo<CompanySummary | null>(() => {
    if (!activeCompanyId) return null;
    const companies = companiesQuery.data?.companies ?? [];
    return companies.find((c) => c.id === activeCompanyId) ?? null;
  }, [activeCompanyId, companiesQuery.data]);

  const value = useMemo<CompanyContextValue>(
    () => ({
      activeCompanyId,
      activeCompany,
      setActiveCompanyId,
      // ready iff the team is ready AND we've hydrated for the current
      // team (or there's definitively no active team).
      ready:
        teamReady &&
        (activeTeamId == null || hydratedForTeam === activeTeamId),
    }),
    [
      activeCompanyId,
      activeCompany,
      setActiveCompanyId,
      teamReady,
      activeTeamId,
      hydratedForTeam,
    ],
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
}

export function useActiveCompany(): CompanyContextValue {
  const ctx = useContext(CompanyContext);
  if (!ctx)
    throw new Error("useActiveCompany must be used inside CompanyProvider");
  return ctx;
}
